import React, { useState } from 'react';
import './QuoteDatePicker.css';
import { FaChevronLeft, FaChevronRight, FaCaretDown } from 'react-icons/fa';

/**
 * QuoteDatePicker — compact, shared calendar modal for all quote forms.
 * Props:
 *   onCancel()           — called when backdrop or Cancel is clicked
 *   onSet(dateString)    — called with "Mon DD, YYYY" string on Confirm
 *   initialDateStr       — existing value string (displayed as "Current booking")
 *   label                — optional label shown in the "Current booking" row
 */
const QuoteDatePicker = ({ onCancel, onSet, initialDateStr = '', label = 'Current booking', minBookingDaysAhead = 0 }) => {
    const getMinDate = () => {
        const minDate = new Date();
        minDate.setHours(0, 0, 0, 0);
        minDate.setDate(minDate.getDate() + minBookingDaysAhead);
        return minDate;
    };

    const parseInitialDate = (dateStr) => {
        const minDate = getMinDate();
        if (!dateStr) return minDate;
        const parsed = new Date(dateStr);
        if (isNaN(parsed.getTime())) return minDate;
        parsed.setHours(0, 0, 0, 0);
        return parsed < minDate ? minDate : parsed;
    };

    const initialDate = parseInitialDate(initialDateStr);
    const minDate = getMinDate();

    const [viewDate, setViewDate] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
    const [selectedDate, setSelectedDate] = useState(initialDate.getDate());
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();



    // Build calendar days array
    const days = [];
    const startDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevMonthTotalDays = new Date(year, month, 0).getDate();

    for (let i = startDay - 1; i >= 0; i--) {
        days.push({ day: prevMonthTotalDays - i, type: 'outside', disabled: true });
    }
    for (let i = 1; i <= totalDays; i++) {
        const cellDate = new Date(year, month, i);
        days.push({ day: i, type: 'current', disabled: cellDate < minDate });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, type: 'outside', disabled: true });
    }

    const isPrevMonthDisabled = year < minDate.getFullYear() || (year === minDate.getFullYear() && month <= minDate.getMonth());

    return (
        <div className="qdp-modal" onClick={onCancel}>
            <div className="qdp-container" onClick={(e) => { e.stopPropagation(); setShowMonthDropdown(false); }}>

                {/* Header */}
                <div className="qdp-header" style={{ position: 'relative' }}>
                    <button
                        className="qdp-nav-btn"
                        disabled={isPrevMonthDisabled}
                        style={{ opacity: isPrevMonthDisabled ? 0.3 : 1, cursor: isPrevMonthDisabled ? 'not-allowed' : 'pointer' }}
                        onClick={() => !isPrevMonthDisabled && setViewDate(new Date(year, month - 1, 1))}
                    >
                        <FaChevronLeft />
                    </button>

                    <div
                        className="qdp-month-year"
                        onClick={(e) => { e.stopPropagation(); setShowMonthDropdown(!showMonthDropdown); }}
                    >
                        {monthNames[month]} {year}
                        <FaCaretDown style={{ fontSize: '11px', marginLeft: '5px', transform: showMonthDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />

                        {showMonthDropdown && (
                            <div className="qdp-month-dropdown" onClick={(e) => e.stopPropagation()}>
                                <div className="qdp-month-grid">
                                    {monthNames.map((m, index) => {
                                        const isMonthDisabled = year === minDate.getFullYear() && index < minDate.getMonth();
                                        return (
                                            <div
                                                key={m}
                                                className={`qdp-month-item ${index === month ? 'active' : ''}`}
                                                onClick={() => {
                                                    if (isMonthDisabled) return;
                                                    setViewDate(new Date(year, index, 1));
                                                    setShowMonthDropdown(false);
                                                }}
                                                style={isMonthDisabled ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                                            >
                                                {m}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="qdp-year-selector" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (year - 1 >= minDate.getFullYear()) setViewDate(new Date(year - 1, month, 1));
                                        }}
                                        style={{ opacity: year - 1 < minDate.getFullYear() ? 0.3 : 1 }}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <span>{year}</span>
                                    <button onClick={(e) => { e.stopPropagation(); setViewDate(new Date(year + 1, month, 1)); }}>
                                        <FaChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <button className="qdp-nav-btn" onClick={() => setViewDate(new Date(year, month + 1, 1))}>
                        <FaChevronRight />
                    </button>
                </div>

                <div className="qdp-hint">Select your preferred booking date</div>

                {/* Weekday headers */}
                <div className="qdp-calendar">
                    <div className="qdp-weekdays">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                            <div key={i} className="qdp-weekday">{d}</div>
                        ))}
                    </div>

                    <div className="qdp-days">
                        {days.map((d, index) => (
                            <div
                                key={index}
                                className={`qdp-day ${d.type} ${d.day === selectedDate && d.type === 'current' ? 'selected' : ''}`}
                                onClick={() => {
                                    if (d.type === 'current' && !d.disabled) {
                                        setSelectedDate(d.day);
                                        const formattedDate = `${monthNames[month]} ${d.day}, ${year}`;
                                        onSet(formattedDate);
                                    }
                                }}
                                style={d.disabled ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                            >
                                {d.day}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuoteDatePicker;
